/**
 * PendingClassificationList — steward review UI for values_registry
 * entries stuck at `value_group: PENDING`.
 *
 * Used inline in the Release Ops Phase 1 Values Engine card and in the
 * Guidance tab's Actions view. Each row shows Emergent's proposed
 * value_group + reasoning, an Accept button, and an Override dropdown
 * (six taxonomy groups + "Propose new value_group").
 *
 * Visible-warning workflow only — never blocks Phase 1.
 */
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, Loader2, CornerDownRight } from "lucide-react";

const NEW_GROUP = "__propose_new__";

function PendingRow({ entry, valueGroups, onChanged }) {
    const [sel, setSel] = useState("");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (payload, okMsg) => {
        setBusy(true);
        try {
            const r = await api.releaseOpsValuesRegistryClassify(payload);
            toast.success(okMsg || `${entry.name} → ${r.action.replace(/-/g, " ")}`);
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Classification failed");
        } finally {
            setBusy(false);
        }
    };

    const flagged = (entry.proposal_notes || "").includes("STEWARD REVIEW")
        || (entry.proposal_notes || "").includes("NEW-GROUP-REQUESTED");

    return (
        <div
            className="border border-amber-700/40 bg-amber-950/10 rounded p-2.5 text-[11px]"
            data-testid={`pending-entry-${entry.name}`}
        >
            <div className="flex items-center gap-2 flex-wrap mb-1">
                <code className="font-bold text-white">{entry.name}</code>
                <span className="pill text-[9px] font-bold uppercase border bg-zinc-800 text-zinc-300 border-zinc-700">
                    {entry.type}
                </span>
                {entry.domain && (
                    <span className="pill text-[9px] font-bold uppercase border bg-zinc-800 text-zinc-400 border-zinc-700">
                        {entry.domain}
                    </span>
                )}
                {flagged && (
                    <span
                        className="pill text-[9px] font-bold uppercase border bg-rose-900/40 text-rose-200 border-rose-700"
                        data-testid={`pending-flag-${entry.name}`}
                    >
                        Needs attention
                    </span>
                )}
            </div>
            <div className="flex items-start gap-1.5 text-zinc-300 mb-2">
                <CornerDownRight className="w-3 h-3 mt-0.5 text-amber-400 shrink-0" />
                <div>
                    <span className="text-zinc-500">Proposed:</span>{" "}
                    <code className="text-fuchsia-300 font-bold" data-testid={`pending-proposed-${entry.name}`}>
                        {entry.proposed_value_group || "—"}
                    </code>
                    {entry.proposal_notes && (
                        <span className="text-zinc-400"> — {entry.proposal_notes}</span>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
                <button
                    type="button"
                    disabled={busy || !entry.proposed_value_group}
                    onClick={() => submit(
                        { name: entry.name, value_group: entry.proposed_value_group },
                        `${entry.name} classified as ${entry.proposed_value_group}`,
                    )}
                    data-testid={`pending-accept-btn-${entry.name}`}
                    className="brutal-btn text-[10px] py-1 px-2.5 bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50 inline-flex items-center gap-1"
                >
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                    Accept
                </button>
                <select
                    value={sel}
                    disabled={busy}
                    onChange={(e) => setSel(e.target.value)}
                    data-testid={`pending-override-select-${entry.name}`}
                    className="brutal-input text-[10px] py-1 px-2 bg-zinc-900 border-zinc-700 max-w-[280px]"
                >
                    <option value="">Override…</option>
                    {valueGroups.map((g) => (
                        <option key={g} value={g}>{g}</option>
                    ))}
                    <option value={NEW_GROUP}>Propose new value_group…</option>
                </select>
                {sel && sel !== NEW_GROUP && (
                    <button
                        type="button"
                        disabled={busy}
                        onClick={() => submit(
                            { name: entry.name, value_group: sel },
                            `${entry.name} overridden to ${sel}`,
                        )}
                        data-testid={`pending-override-apply-btn-${entry.name}`}
                        className="brutal-btn text-[10px] py-1 px-2.5 disabled:opacity-50"
                    >
                        Apply override
                    </button>
                )}
                {sel === NEW_GROUP && (
                    <span className="flex items-center gap-1.5 flex-wrap">
                        <input
                            type="text"
                            value={note}
                            disabled={busy}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Why a new value_group?"
                            data-testid={`pending-newgroup-note-input-${entry.name}`}
                            className="brutal-input text-[10px] py-1 px-2 w-52"
                        />
                        <button
                            type="button"
                            disabled={busy || !note.trim()}
                            onClick={() => submit(
                                { name: entry.name, action: "propose_new", note: note.trim() },
                                `${entry.name} flagged for a new value_group`,
                            )}
                            data-testid={`pending-newgroup-submit-btn-${entry.name}`}
                            className="brutal-btn text-[10px] py-1 px-2.5 disabled:opacity-50"
                        >
                            Request
                        </button>
                    </span>
                )}
            </div>
        </div>
    );
}

export function PendingClassificationList({ entries, valueGroups, onChanged }) {
    if (!entries?.length) {
        return (
            <p className="text-[11px] text-zinc-500 italic" data-testid="pending-classification-empty">
                No entries pending classification.
            </p>
        );
    }
    return (
        <div className="space-y-2" data-testid="pending-classification-list">
            {entries.map((e) => (
                <PendingRow key={e.name} entry={e} valueGroups={valueGroups} onChanged={onChanged} />
            ))}
        </div>
    );
}
